import { DEFAULT_RENT_INCREASE_INTERVAL_MONTHS, formatEuro, formatInteger } from '@brimo/shared';
import type { RentTotals } from './types';

/**
 * Rent of a unit as a fact (owner decision 2026-10-11, ABWEICHUNGEN D-165/D-166): the actual rent
 * („Ist-Miete") is the fact, the target rent („Soll-Miete") is what the calculator uses, the rent subsidy
 * („Mietsubvention") is a one-time amount shown for information only and never part of a calculated
 * figure. The same rows and sentence as the calculator PDF (apps/api deals/rent-facts.ts).
 */

export interface UnitRentFacts {
  actualCents: number | null;
  targetCents: number | null;
  subsidyCents: number | null;
  /** „Subventionszyklen": rent-increase intervals the subsidy covers (owner decision 19.10.2026). */
  subsidyCycles?: number | null | undefined;
}

export interface RentRow {
  label: string;
  value: string;
}

const EMPTY = '—';

/**
 * „Kaltmiete (Ist)" always; „Soll-Miete (Kalkulation)" and „Mietsubvention" only when the unit has a value;
 * „Subventionszyklen" („2 × 36 Monate") after the subsidy when the unit has cycles.
 */
export function rentFactRows(facts: UnitRentFacts): RentRow[] {
  const rows: RentRow[] = [
    {
      label: 'Kaltmiete (Ist)',
      value: facts.actualCents === null ? EMPTY : `${formatEuro(facts.actualCents)} / Monat`,
    },
  ];
  if (facts.targetCents !== null) {
    rows.push({ label: 'Soll-Miete (Kalkulation)', value: `${formatEuro(facts.targetCents)} / Monat` });
  }
  if (facts.subsidyCents !== null) {
    rows.push({ label: 'Mietsubvention', value: `${formatEuro(facts.subsidyCents)} einmalig` });
  }
  if (facts.subsidyCycles !== null && facts.subsidyCycles !== undefined && facts.subsidyCycles > 0) {
    rows.push({
      label: 'Subventionszyklen',
      value: `${formatInteger(facts.subsidyCycles)} × ${DEFAULT_RENT_INCREASE_INTERVAL_MONTHS} Monate`,
    });
  }
  return rows;
}

/** The property's sums over its units; only the sums that have a value. */
export function rentTotalRows(totals: RentTotals): RentRow[] {
  const rows: RentRow[] = [];
  if (totals.actualCents !== null) {
    rows.push({ label: 'Ist-Miete mtl. gesamt', value: formatEuro(totals.actualCents) });
  }
  if (totals.targetCents !== null) {
    rows.push({ label: 'Soll-Miete mtl. gesamt', value: formatEuro(totals.targetCents) });
  }
  if (totals.subsidyCents !== null) {
    rows.push({ label: 'Mietsubvention gesamt', value: formatEuro(totals.subsidyCents) });
  }
  return rows;
}

/**
 * The hint next to the calculated rent when it differs from the actual rent: „Kalkuliert mit der
 * Soll-Miete von 687,70 € / Monat. Aktuelle Ist-Miete: 520 € / Monat." plus the subsidy when there is
 * one. `calculatedRentCents` is the calculator's current rent input. Null without an actual rent or when
 * both are the same.
 */
export function rentBasisHint(
  calculatedRentCents: number,
  actualCents: number | null,
  subsidyCents: number | null,
): string | null {
  if (actualCents === null || actualCents === calculatedRentCents) return null;
  const hint = `Kalkuliert mit der Soll-Miete von ${formatEuro(calculatedRentCents)} / Monat. Aktuelle Ist-Miete: ${formatEuro(actualCents)} / Monat.`;
  return subsidyCents === null ? hint : `${hint} Mietsubvention: ${formatEuro(subsidyCents)} einmalig.`;
}
